import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { and, eq } from "drizzle-orm";
import { Flag, Maximize } from "lucide-react";
import { useCallback } from "react";
import { getDb } from "@/db";
import { cars, events, heatAssignments, heats } from "@/db/schema";

// ── Server functions ─────────────────────────────────────────────────

const getStagingData = createServerFn({ method: "GET" })
	.inputValidator((eventId: string) => eventId)
	.handler(async ({ data: eventId }) => {
		const db = getDb();
		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, Number(eventId)))
			.get();
		if (!event) throw new Error("Event not found");

		const eventCars = await db
			.select()
			.from(cars)
			.where(eq(cars.eventId, event.id));

		// Get next 3 scheduled heats
		const scheduledHeats = await db
			.select()
			.from(heats)
			.where(and(eq(heats.eventId, event.id), eq(heats.status, "scheduled")));

		const upcomingHeats = scheduledHeats
			.sort((a, b) => a.heatNumber - b.heatNumber)
			.slice(0, 3);

		let assignments: Array<{
			id: number;
			heatId: number;
			lane: number;
			carId: number;
		}> = [];
		for (const heat of upcomingHeats) {
			const ha = await db
				.select()
				.from(heatAssignments)
				.where(eq(heatAssignments.heatId, heat.id));
			assignments = assignments.concat(ha);
		}

		// Get current racing heat
		const racingHeat = await db
			.select()
			.from(heats)
			.where(and(eq(heats.eventId, event.id), eq(heats.status, "racing")))
			.get();

		const completedCount = (
			await db
				.select()
				.from(heats)
				.where(and(eq(heats.eventId, event.id), eq(heats.status, "complete")))
		).length;

		const totalHeats = (
			await db.select().from(heats).where(eq(heats.eventId, event.id))
		).length;

		return {
			event,
			cars: eventCars,
			upcomingHeats,
			assignments,
			racingHeat,
			completedCount,
			totalHeats,
		};
	});

// ── Route ────────────────────────────────────────────────────────────

export const Route = createFileRoute("/events/$eventId/staging")({
	loader: ({ params }) => getStagingData({ data: params.eventId }),
	component: StagingDisplay,
});

function StagingDisplay() {
	const params = Route.useParams();
	const initialData = Route.useLoaderData();

	const { data } = useQuery({
		queryKey: ["staging", params.eventId],
		queryFn: () => getStagingData({ data: params.eventId }),
		initialData,
		refetchInterval: 3000,
	});

	const {
		event,
		cars: eventCars,
		upcomingHeats,
		assignments,
		completedCount,
		totalHeats,
	} = data;

	const handleFullscreen = useCallback(() => {
		if (document.fullscreenElement) {
			document.exitFullscreen();
		} else {
			document.documentElement.requestFullscreen();
		}
	}, []);

	return (
		<div className="fixed inset-0 z-50 bg-background overflow-hidden flex flex-col">
			{/* Header */}
			<div className="flex items-center justify-between px-6 pt-5 pb-3">
				<div className="flex items-center gap-4">
					{event.imageUrl ? (
						<img
							src={event.imageUrl}
							alt={event.name}
							className="h-14 w-auto rounded-lg object-cover"
						/>
					) : (
						<Flag size={32} className="text-cherry" />
					)}
					<div>
						<h1 className="text-3xl text-foreground font-bold">On Deck</h1>
						<p className="text-muted-foreground text-sm">
							{event.name} &mdash; {completedCount}/{totalHeats} heats complete
						</p>
					</div>
				</div>
				<button
					type="button"
					onClick={handleFullscreen}
					className="p-2 text-muted-foreground hover:text-foreground rounded-lg transition-colors"
					title="Toggle fullscreen"
				>
					<Maximize size={18} />
				</button>
			</div>

			{/* Content */}
			<div className="flex-1 p-6 overflow-auto">
				{upcomingHeats.length === 0 ? (
					<div className="flex items-center justify-center h-full">
						<div className="text-center">
							<p className="text-foreground text-3xl font-bold">
								{totalHeats === 0
									? "No schedule generated"
									: "All heats complete!"}
							</p>
							<p className="text-muted-foreground text-lg mt-2">
								{totalHeats === 0
									? "Generate a schedule from the admin panel"
									: `${completedCount} heats raced`}
							</p>
						</div>
					</div>
				) : (
					<div className="max-w-7xl mx-auto space-y-6">
						{upcomingHeats.map((heat, heatIdx) => {
							const heatAssigns = assignments
								.filter((a) => a.heatId === heat.id)
								.sort((a, b) => a.lane - b.lane);
							const isNext = heatIdx === 0;

							return (
								<div
									key={heat.id}
									className={`rounded-2xl border-2 p-6 ${
										isNext
											? "border-primary bg-primary/5"
											: "border-border bg-card/30"
									}`}
								>
									<div className="flex items-center gap-3 mb-4">
										{isNext && (
											<span className="px-3 py-1 bg-primary text-primary-foreground text-sm font-black uppercase rounded-full">
												Next
											</span>
										)}
										<span className="text-foreground font-bold text-2xl">
											Heat #{heat.heatNumber}
										</span>
										{heat.division && (
											<span className="px-3 py-1 bg-primary/10 text-primary text-sm font-bold rounded-full">
												{heat.division}
											</span>
										)}
									</div>

									<div
										className={`grid gap-4 ${
											heatAssigns.length <= 4
												? "grid-cols-2 sm:grid-cols-4"
												: "grid-cols-2 sm:grid-cols-4 lg:grid-cols-6"
										}`}
									>
										{heatAssigns.map((assign) => {
											const car = eventCars.find((c) => c.id === assign.carId);
											if (!car) return null;

											return (
												<div
													key={assign.id}
													className={`rounded-xl p-4 flex flex-col items-center text-center ${
														isNext
															? "bg-card border border-border"
															: "bg-background/50"
													}`}
												>
													<span className="text-muted-foreground font-bold uppercase text-xs mb-2">
														Lane {assign.lane}
													</span>
													{car.avatarUrl ? (
														<img
															src={car.avatarUrl}
															alt=""
															className={`rounded-xl object-cover mb-2 ${isNext ? "w-20 h-20" : "w-16 h-16"}`}
														/>
													) : (
														<div
															className={`rounded-xl bg-muted flex items-center justify-center text-muted-foreground font-bold mb-2 ${isNext ? "w-20 h-20 text-3xl" : "w-16 h-16 text-2xl"}`}
														>
															{car.carName?.charAt(0) ?? "#"}
														</div>
													)}
													<p
														className={`text-foreground font-black truncate w-full ${isNext ? "text-lg" : "text-base"}`}
													>
														<span className="text-sm font-bold text-primary bg-primary/10 rounded px-1.5 py-0.5 mr-1">
															#{car.carNumber}
														</span>
														{car.carName}
													</p>
													<p className="text-muted-foreground text-sm truncate w-full">
														{car.driverName}
													</p>
													{car.division && (
														<span className="mt-1 inline-flex items-center px-1.5 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded">
															{car.division}
														</span>
													)}
												</div>
											);
										})}
									</div>
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}
